// v8.8b — BRR forced-symbol chip picker.
//
// Lets the user pin/unpin which symbols ALWAYS participate in the BRR
// (Breakout & Retest) scan rotation regardless of the soft-blacklist or
// PERMANENT_BLACKLIST. Pinned symbols also override the runtime auto-
// blacklist's WR-based ban so a high-conviction commission generator
// (BTC, ETH, XAU, XAG by default) stays in rotation no matter what.
//
// API contract:
//   GET  /api/bot/brr-forced/:account_id  → {forced_symbols: string[], is_default: bool}
//   PUT  /api/bot/brr-forced/:account_id  body: {symbols: string[]}
import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, X, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Curated suggestion palette — these are the symbols the BRR scanner has
// historically generated commission on. User can pin any subset, or add
// a custom symbol via the free-text input.
const SUGGESTED = [
  "cryBTCUSD",
  "cryETHUSD",
  "frxXAUUSD",
  "frxXAGUSD",
  "frxXAUEUR",
  "frxXAUGBP",
  "frxXPDUSD",
  "frxEURUSD",
  "frxGBPUSD",
  "frxUSDJPY",
];

// Pretty-printer — Deriv symbol IDs are cryptic, so we render a readable
// label inline while keeping the canonical ID for the API call.
function prettySymbol(sym) {
  if (sym.startsWith("cry")) return sym.replace("cry", "").replace("USD", "/USD");
  if (sym.startsWith("frx")) {
    const body = sym.slice(3);
    if (body.length === 6) return `${body.slice(0, 3)}/${body.slice(3)}`;
    return body;
  }
  return sym;
}

export function BrrForcedPicker({ accountId, running }) {
  const [forced, setForced] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);

  const fetchForced = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await api.get(`/bot/brr-forced/${encodeURIComponent(accountId)}`);
      setForced(res.data?.forced_symbols || []);
      setIsDefault(Boolean(res.data?.is_default));
      setDirty(false);
    } catch {
      // silent — first load before bot has been started yet
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchForced();
  }, [fetchForced]);

  const togglePin = useCallback((sym) => {
    setForced((cur) => {
      const next = cur.includes(sym) ? cur.filter((s) => s !== sym) : [...cur, sym];
      return next.sort();
    });
    setDirty(true);
  }, []);

  const addCustom = useCallback(() => {
    const v = draft.trim();
    if (!v) return;
    if (forced.includes(v)) {
      toast.info(`${prettySymbol(v)} is already pinned`);
      return;
    }
    setForced((cur) => [...cur, v].sort());
    setDraft("");
    setDirty(true);
  }, [draft, forced]);

  const save = useCallback(async () => {
    if (!accountId) return;
    setSaving(true);
    try {
      const res = await api.put(`/bot/brr-forced/${encodeURIComponent(accountId)}`, {
        symbols: forced,
      });
      setForced(res.data?.forced_symbols || forced);
      setIsDefault(false);
      setDirty(false);
      toast.success(
        forced.length > 0
          ? `Pinned ${forced.length} symbol${forced.length === 1 ? "" : "s"} to BRR`
          : "BRR forced set cleared",
      );
    } catch (e) {
      toast.error("Failed to save BRR symbols", {
        description: e?.message || "Backend rejected the request",
      });
    } finally {
      setSaving(false);
    }
  }, [accountId, forced]);

  const resetDefault = useCallback(async () => {
    setForced(["cryBTCUSD", "cryETHUSD", "frxXAGUSD", "frxXAUUSD"]);
    setDirty(true);
  }, []);

  // Unpinned suggestions = SUGGESTED \ forced. Sorted to keep the list stable.
  const unpinnedSuggestions = useMemo(
    () => SUGGESTED.filter((s) => !forced.includes(s)),
    [forced],
  );

  return (
    <div
      data-testid="brr-forced-picker"
      className="mt-4 bg-vts-bg/60 border border-vts-line rounded-md px-4 py-3"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
          BRR pinned symbols
          {isDefault && (
            <span className="ml-2 text-vts-blue/70 normal-case lowercase">(using defaults)</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {dirty && (
            <button
              data-testid="brr-forced-save"
              onClick={save}
              disabled={saving}
              className="text-[9px] font-mono px-2 py-1 rounded bg-vts-blue/20 text-vts-blue border border-vts-blue/40 hover:bg-vts-blue/30 transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          )}
          <button
            data-testid="brr-forced-reset"
            onClick={resetDefault}
            title="Reset to BTC + ETH + Gold + Silver"
            className="text-[9px] font-mono px-2 py-1 rounded text-vts-silver/60 border border-vts-line hover:text-vts-silver hover:border-vts-silver/40 transition-colors flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            Default
          </button>
        </div>
      </div>

      {/* Pinned chips */}
      {loading ? (
        <div className="text-[10px] font-mono text-vts-silver/40 py-2">Loading…</div>
      ) : forced.length === 0 ? (
        <div
          data-testid="brr-forced-empty"
          className="text-[10px] font-mono text-vts-silver/50 py-2"
        >
          No pinned symbols. BRR will only scan the dynamic universe.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {forced.map((s) => (
            <button
              key={s}
              data-testid={`brr-forced-chip-${s}`}
              onClick={() => togglePin(s)}
              className="group inline-flex items-center gap-1 px-2 py-1 rounded-md bg-vts-blue/15 border border-vts-blue/40 text-vts-blue text-[10px] font-mono hover:bg-vts-blue/25 transition-colors"
              title={`Unpin ${s}`}
            >
              {prettySymbol(s)}
              <X className="w-3 h-3 opacity-60 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      )}

      {/* Quick-add suggestions */}
      {unpinnedSuggestions.length > 0 && (
        <div className="mb-3">
          <div className="text-[9px] font-mono text-vts-silver/50 mb-1">Suggested:</div>
          <div className="flex flex-wrap gap-1">
            {unpinnedSuggestions.map((s) => (
              <button
                key={s}
                data-testid={`brr-forced-suggest-${s}`}
                onClick={() => togglePin(s)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-vts-line/60 text-vts-silver/60 text-[9px] font-mono hover:border-vts-blue/60 hover:text-vts-silver transition-colors"
              >
                <Plus className="w-2.5 h-2.5" />
                {prettySymbol(s)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom symbol input */}
      <div className="flex items-center gap-2">
        <input
          data-testid="brr-forced-input"
          type="text"
          placeholder="Custom symbol (e.g. frxEURJPY)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          className="flex-1 bg-vts-bg/40 border border-vts-line rounded px-2 py-1 text-[10px] font-mono text-vts-silver placeholder:text-vts-silver/30 focus:outline-none focus:border-vts-blue/60"
        />
        <button
          data-testid="brr-forced-add"
          onClick={addCustom}
          disabled={!draft.trim()}
          className="text-[9px] font-mono px-2 py-1 rounded border border-vts-line text-vts-silver/70 hover:text-vts-silver hover:border-vts-silver/40 transition-colors disabled:opacity-40"
        >
          Add
        </button>
      </div>

      <div className="mt-2 pt-2 border-t border-vts-line/40 text-[9px] font-mono text-vts-silver/40">
        Pinned symbols bypass the soft-blacklist and always appear in the BRR rotation.
        {running ? " Changes apply to the live runner instantly." : " Pin now — applies when you start the bot."}
      </div>
    </div>
  );
}

export default BrrForcedPicker;
